/**
 * i18n configuration for internationalization
 * Default language: Afrikaans (af)
 * Supported languages: Afrikaans (af), English (en)
 *
 * Translations are organized by module for easier management.
 */

import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import English modules
import enApp from './locales/en/app.json';
import enAuth from './locales/en/auth.json';
import enFamily from './locales/en/family.json';
import enProfile from './locales/en/profile.json';
import enContent from './locales/en/content.json';
import enMessages from './locales/en/messages.json';
import enTokens from './locales/en/tokens.json';
import enGifting from './locales/en/gifting.json';
import enStars from './locales/en/stars.json';
import enErrors from './locales/en/errors.json';
import enOffline from './locales/en/offline.json';
import enAdmin from './locales/en/admin.json';
import enRecordings from './locales/en/recordings.json';
import enProgress from './locales/en/progress.json';
import enSession from './locales/en/session.json';
import enSettings from './locales/en/settings.json';
import enInvitation from './locales/en/invitation.json';
import enCms from './locales/en/cms.json';
import enSubscription from './locales/en/subscription.json';
import enOnboarding from './locales/en/onboarding.json';
import enFeedback from './locales/en/feedback.json';

// Import Afrikaans modules
import afApp from './locales/af/app.json';
import afAuth from './locales/af/auth.json';
import afFamily from './locales/af/family.json';
import afProfile from './locales/af/profile.json';
import afContent from './locales/af/content.json';
import afMessages from './locales/af/messages.json';
import afTokens from './locales/af/tokens.json';
import afGifting from './locales/af/gifting.json';
import afStars from './locales/af/stars.json';
import afErrors from './locales/af/errors.json';
import afOffline from './locales/af/offline.json';
import afAdmin from './locales/af/admin.json';
import afRecordings from './locales/af/recordings.json';
import afProgress from './locales/af/progress.json';
import afSession from './locales/af/session.json';
import afSettings from './locales/af/settings.json';
import afInvitation from './locales/af/invitation.json';
import afCms from './locales/af/cms.json';
import afSubscription from './locales/af/subscription.json';
import afOnboarding from './locales/af/onboarding.json';
import afFeedback from './locales/af/feedback.json';

// Merge all modules into single translation objects
const en = {
  ...enApp,
  ...enAuth,
  ...enFamily,
  ...enProfile,
  ...enContent,
  ...enMessages,
  ...enTokens,
  ...enGifting,
  ...enStars,
  ...enErrors,
  ...enOffline,
  ...enAdmin,
  ...enRecordings,
  ...enProgress,
  ...enSession,
  ...enSettings,
  ...enInvitation,
  ...enCms,
  ...enSubscription,
  ...enOnboarding,
  ...enFeedback,
};

const af = {
  ...afApp,
  ...afAuth,
  ...afFamily,
  ...afProfile,
  ...afContent,
  ...afMessages,
  ...afTokens,
  ...afGifting,
  ...afStars,
  ...afErrors,
  ...afOffline,
  ...afAdmin,
  ...afRecordings,
  ...afProgress,
  ...afSession,
  ...afSettings,
  ...afInvitation,
  ...afCms,
  ...afSubscription,
  ...afOnboarding,
  ...afFeedback,
};

/**
 * Initialize i18next with Afrikaans as default
 */
export async function initI18n() {
  // Check if user has a language preference, if not set Afrikaans as default
  const savedLanguage = localStorage.getItem('i18nextLng');
  if (!savedLanguage) {
    localStorage.setItem('i18nextLng', 'af');
  }

  await i18next.use(LanguageDetector).init({
    fallbackLng: 'af',
    defaultNS: 'common',
    debug: import.meta.env.DEV,
    returnObjects: true, // Enable returning objects for nested translations
    resources: {
      af: {
        common: af,
      },
      en: {
        common: en,
      },
    },
    detection: {
      order: ['localStorage', 'navigator'], // Check localStorage first, then browser
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    lng: savedLanguage || 'af', // Force Afrikaans if no saved preference
  });

  console.log('i18n initialized with language:', i18next.language);
  return i18next;
}

/**
 * Get translated text
 */
export function t(key, options) {
  return i18next.t(key, options);
}

/**
 * Change language
 */
export function changeLanguage(lang) {
  return i18next.changeLanguage(lang);
}

/**
 * Get current language
 */
export function getCurrentLanguage() {
  return i18next.language || 'af';
}

/**
 * Export i18next instance for direct access
 */
export { i18next };
